import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Switch } from '../../../ui/switch'
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group'
import { useSidebarCollapsed } from '../SidebarContext'
import { useSetting } from '../../../../lib/db/hooks'
import type { HappyMode, HappyModeConfig } from './HappyModusDropdown.types'
import './HappyModusDropdown.scss'

const MODES: HappyModeConfig[] = [
  {
    id: 'dynamic',
    nameKey: 'sidebar.happyMode.dynamic.name',
    subtitleKey: 'sidebar.happyMode.dynamic.subtitle',
    descriptionKey: 'sidebar.happyMode.dynamic.description',
  },
  {
    id: 'focused',
    nameKey: 'sidebar.happyMode.focused.name',
    subtitleKey: 'sidebar.happyMode.focused.subtitle',
    descriptionKey: 'sidebar.happyMode.focused.description',
  },
  {
    id: 'mindful',
    nameKey: 'sidebar.happyMode.mindful.name',
    subtitleKey: 'sidebar.happyMode.mindful.subtitle',
    descriptionKey: 'sidebar.happyMode.mindful.description',
  },
]

/**
 * Mode icon component — renders the exact Figma SVG icon for each Happy Mode.
 * Uses currentColor so icons inherit the space color from the parent CSS (--space-500).
 */
const InfoIcon: React.FC<{ size?: number; className?: string }> = ({ size = 17, className }) => (
  <svg width={size} height={size} viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M8.17 15.66C12.31 15.66 15.66 12.31 15.66 8.16C15.66 4.02 12.31 0.66 8.17 0.66C4.02 0.66 0.67 4.02 0.67 8.16C0.67 12.31 4.02 15.66 8.17 15.66Z" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8.17 5.16C8.72 5.16 9.17 4.72 9.17 4.16C9.17 3.61 8.72 3.16 8.17 3.16C7.61 3.16 7.17 3.61 7.17 4.16C7.17 4.72 7.61 5.16 8.17 5.16Z" fill="currentColor"/>
    <path d="M8.17 12.66V7.16" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const ModeIcon: React.FC<{ mode: HappyMode; size?: number }> = ({ mode, size = 16 }) => {
  switch (mode) {
    case 'dynamic':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 16C7.65 16 7.31 15.86 7.06 15.61C6.81 15.36 6.67 15.02 6.67 14.67C6.67 13.25 6.1 11.9 5.1 10.9C4.1 9.9 2.75 9.33 1.33 9.33C0.98 9.33 0.64 9.19 0.39 8.94C0.14 8.69 0 8.35 0 8C0 7.65 0.14 7.31 0.39 7.06C0.64 6.81 0.98 6.67 1.33 6.67C2.75 6.67 4.1 6.1 5.1 5.1C6.1 4.1 6.67 2.75 6.67 1.33C6.67 0.98 6.81 0.64 7.06 0.39C7.31 0.14 7.65 0 8 0C8.35 0 8.69 0.14 8.94 0.39C9.19 0.64 9.33 0.98 9.33 1.33C9.33 2.75 9.9 4.1 10.9 5.1C11.9 6.1 13.25 6.67 14.67 6.67C15.02 6.67 15.36 6.81 15.61 7.06C15.86 7.31 16 7.65 16 8C16 8.35 15.86 8.69 15.61 8.94C15.36 9.19 15.02 9.33 14.67 9.33C13.25 9.33 11.9 9.9 10.9 10.9C9.9 11.9 9.33 13.25 9.33 14.67C9.33 15.02 9.19 15.36 8.94 15.61C8.69 15.86 8.35 16 8 16Z" fill="currentColor" />
        </svg>
      )
    case 'focused':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 0C6.42 0 4.87 0.47 3.56 1.35C2.24 2.23 1.21 3.48 0.61 4.94C0 6.4 -0.15 8.01 0.15 9.56C0.46 11.11 1.22 12.54 2.34 13.66C3.46 14.78 4.89 15.54 6.44 15.85C7.99 16.16 9.6 16 11.06 15.39C12.52 14.79 13.77 13.76 14.65 12.44C15.53 11.13 16 9.58 16 8C16 5.88 15.15 3.85 13.65 2.35C12.15 0.85 10.12 0 8 0ZM8 14C6.92 14 5.87 13.71 4.94 13.16C4.02 12.61 3.26 11.83 2.74 10.88C3.16 10.96 3.58 11 4 11C5.86 11 7.64 10.26 8.95 8.95C10.26 7.64 11 5.86 11 4C11 3.58 10.96 3.16 10.88 2.74C12.05 3.38 12.97 4.39 13.5 5.61C14.03 6.83 14.14 8.2 13.81 9.49C13.48 10.78 12.73 11.92 11.68 12.74C10.62 13.56 9.33 14 8 14Z" fill="currentColor" />
        </svg>
      )
    case 'mindful':
      return (
        <svg width={size} height={size} viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 8V14C10.12 14 12.16 13.16 13.66 11.66C15.16 10.16 16 8.12 16 6V0C13.88 0 11.84 0.84 10.34 2.34C8.84 3.84 8 5.88 8 8Z" fill="currentColor" />
          <path d="M0 5V8C0 9.59 0.63 11.12 1.76 12.24C2.88 13.37 4.41 14 6 14V11C6 9.41 5.37 7.88 4.24 6.76C3.12 5.63 1.59 5 0 5Z" fill="currentColor" />
        </svg>
      )
    default:
      return null
  }
}

/**
 * HappyModusDropdown — Dropdown at the top of the StandardSidebar
 * for selecting the active Happy Mode (Dynamic, Focused, Mindful).
 *
 * Uses Radix RadioGroup for proper radio semantics and keyboard navigation.
 * Panel is rendered via React portal to escape the sidebar's overflow:hidden.
 * Persists selection via Dexie + Supabase sync.
 * Keyboard shortcut: J to toggle open/close.
 */
export const HappyModusDropdown: React.FC = () => {
  const { t } = useTranslation()
  const isCollapsed = useSidebarCollapsed()
  const { value: mode, setValue: setMode } = useSetting<HappyMode>('happyMode', 'dynamic')
  const { value: isExpanded, setValue: setIsExpanded } = useSetting<boolean>('happyModeExpanded', false)
  const [isOpen, setIsOpen] = useState(false)
  const [panelPos, setPanelPos] = useState({ top: -9999, left: -9999, maxHeight: 0 })
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const currentConfig = MODES.find(m => m.id === mode) ?? MODES[0]

  // Recalculate panel position from trigger rect
  const updatePanelPos = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const margin = 16
    if (isCollapsed) {
      setPanelPos({ top: rect.top, left: rect.right + 4, maxHeight: window.innerHeight - rect.top - margin })
    } else {
      const top = rect.bottom + 4
      setPanelPos({ top, left: rect.left, maxHeight: window.innerHeight - top - margin })
    }
  }, [isCollapsed])

  // Position the panel relative to the trigger via fixed positioning
  useLayoutEffect(() => {
    if (isOpen) updatePanelPos()
  }, [isOpen, updatePanelPos])

  // Keep panel position in sync on scroll/resize
  useEffect(() => {
    if (!isOpen) return
    window.addEventListener('resize', updatePanelPos)
    window.addEventListener('scroll', updatePanelPos, true)
    return () => {
      window.removeEventListener('resize', updatePanelPos)
      window.removeEventListener('scroll', updatePanelPos, true)
    }
  }, [isOpen, updatePanelPos])

  // Keyboard shortcut "J" to cycle through modes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault()
        const currentIndex = MODES.findIndex(m => m.id === mode)
        const nextIndex = e.shiftKey
          ? (currentIndex - 1 + MODES.length) % MODES.length
          : (currentIndex + 1) % MODES.length
        setMode(MODES[nextIndex].id)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [mode, setMode])

  // Escape key to close panel
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen])

  // Click-outside to close (checks trigger button and portaled panel)
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        panelRef.current && !panelRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  return (
    <div className="happy-modus-dropdown" ref={wrapperRef}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        className={`happy-modus-dropdown__trigger${isOpen ? ' happy-modus-dropdown__trigger--open' : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {isCollapsed ? (
          <span className="happy-modus-dropdown__trigger-icon">
            <ModeIcon mode={currentConfig.id} size={16} />
          </span>
        ) : (
          <>
            <div className="happy-modus-dropdown__trigger-group">
              <span className="happy-modus-dropdown__trigger-icon">
                <ModeIcon mode={currentConfig.id} size={16} />
              </span>
              <span className="happy-modus-dropdown__trigger-label">{t(currentConfig.nameKey)}</span>
              <span className="happy-modus-dropdown__trigger-shortcut">J</span>
            </div>
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="happy-modus-dropdown__trigger-chevron" aria-hidden="true">
              <path d="M4.67 10L8 13.33L11.33 10M4.67 6L8 2.67L11.33 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </>
        )}
      </button>

      {/* Panel — rendered via portal to escape sidebar overflow:hidden */}
      {isOpen && createPortal(
        <div
          ref={panelRef}
          className={`happy-modus-dropdown__panel${isExpanded ? ' happy-modus-dropdown__panel--expanded' : ''}`}
          role="dialog"
          aria-label={t('sidebar.happyMode.label')}
          style={{ top: panelPos.top, left: panelPos.left, maxHeight: panelPos.maxHeight }}
        >
          {/* Header — fixed above scroll */}
          <div className="happy-modus-dropdown__header">
            {t('sidebar.happyMode.currentMode')}
          </div>
          <div className="happy-modus-dropdown__separator" />

          {/* Scrollable content — no horizontal padding, children own their padding */}
          <div className="happy-modus-dropdown__content">
            <RadioGroup
              value={mode ?? 'dynamic'}
              onValueChange={(val) => {
                setMode(val as HappyMode)
                setIsOpen(false)
              }}
              className="happy-modus-dropdown__modes"
            >
              {MODES.map(cfg => {
                const isSelected = mode === cfg.id
                return (
                  <RadioGroupItem
                    key={cfg.id}
                    value={cfg.id}
                    className={`happy-modus-dropdown__mode-card${isSelected ? ' happy-modus-dropdown__mode-card--selected' : ''}`}
                  >
                    <span className="happy-modus-dropdown__mode-radio">
                      <span className="happy-modus-dropdown__mode-radio-dot" />
                    </span>
                    <div className="happy-modus-dropdown__mode-selector">
                      <span className="happy-modus-dropdown__mode-icon">
                        <ModeIcon mode={cfg.id} size={16} />
                      </span>
                      <div className="happy-modus-dropdown__mode-text">
                        <div className="happy-modus-dropdown__mode-header">
                          <span className="happy-modus-dropdown__mode-name">{t(cfg.nameKey)}</span>
                          {!isExpanded && (
                            <span className="happy-modus-dropdown__mode-subtitle-inline">
                              {' – '}{t(cfg.subtitleKey)}
                            </span>
                          )}
                        </div>
                        {isExpanded && (
                          <>
                            <span className="happy-modus-dropdown__mode-subtitle">
                              {t(cfg.subtitleKey)}
                              <svg className="happy-modus-dropdown__mode-subtitle-chevron" width={10} height={10} viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 3.75L5 6.25L7.5 3.75" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                            <span className="happy-modus-dropdown__mode-description">{t(cfg.descriptionKey)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </RadioGroupItem>
                )
              })}
            </RadioGroup>

            {/* Info box (only in expanded mode) */}
            {isExpanded && (
              <>
                <div className="happy-modus-dropdown__info-separator" />
                <div className="happy-modus-dropdown__info-box">
                  <InfoIcon className="happy-modus-dropdown__info-box-icon" />
                  <div className="happy-modus-dropdown__info-box-text">
                    <span className="happy-modus-dropdown__info-box-title">{t('sidebar.happyMode.infoTitle')}</span>
                    <span className="happy-modus-dropdown__info-box-description">{t('sidebar.happyMode.infoDescription')}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="happy-modus-dropdown__footer">
            <button className="happy-modus-dropdown__customize" onClick={() => { /* TODO: settings page */ }}>
              <Settings size={16} />
              <span>{t('sidebar.happyMode.customize')}</span>
            </button>
            <div className="happy-modus-dropdown__footer-actions">
              <button
                className="happy-modus-dropdown__info-btn"
                aria-label={t('sidebar.happyMode.info')}
                onClick={() => setIsExpanded(!(isExpanded ?? false))}
              >
                <InfoIcon />
              </button>
              <Switch
                className="happy-modus-dropdown__switch"
                checked={isExpanded ?? false}
                onCheckedChange={val => setIsExpanded(val)}
                aria-label={t('sidebar.happyMode.expandedView')}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
